import { Box, useTheme, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SaveIcon from '@mui/icons-material/Save';
import { Help as HelpIcon, CheckCircle, Cancel } from '@mui/icons-material';
import { tokens } from "../../../../../theme";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Text from "../../../../theme/text/Text";
import Dropdown from "../../../../theme/dropout/DropOut";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";
import { useEditWebshopCredentialsMutation } from "../../../../../newapi/webshop/webshopSlice";
import { showToastMessageUpdateRequest, showToastMessageRequestError } from "../../../../../utils/toasts";
import { 
    getMarketplaceCapabilities, 
    getSyncOptionConfigurations, 
    getDefaultSyncSettings,
    validateSyncConfiguration,
    createSyncStateUpdater
} from "../../../ModalsProgression/MpAddWebshop/utils/etailize/etailizeHelpers";
import cx from 'classnames';
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return props.hasModifyPermission ? 
        <ButtonGroup version={2}>
            <Shbutton
                isLoading={props.isLoading}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<SaveIcon />}
            >
                {t('save')}
            </Shbutton>
        </ButtonGroup> : <></>;
};

// Render advanced sync option toggle with capability awareness
const EtailizeSyncOption = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const isEnabled = props.propagationSettings[props.config.key] === 'true' || props.propagationSettings[props.config.key] === true;
    const hasError = props.syncValidationErrors.some(error => error.field === props.config.key);

    const Newicon = props.config.newIcon;
    return (
        <Box
            sx={{
                width:1,
            }}
        >
            <Box 
                // key={props.config.key}
                sx={{
                    width: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                    pr:1,
                    borderRadius:8,
                    gap:2,
                    opacity: props.config.available ? 1 : 0.6
                }}
            >
                {/* Option Info */}
                <Box
                    sx={{
                        py:1,
                        pl:1,
                        display:'flex',
                        flex:1,
                        alignItems:"center",
                        gap:2,
                        overflowX:"hidden"
                    }}
                >
                    <Box
                        sx={{
                            width:32,
                            height:32,
                            position:"relative",
                            background:props.config.available ? colors.green[100] : colors.red[100],
                            color:props.config.available ? colors.green[400] : colors.red[400],
                            display:'flex',
                            alignItems:'center',
                            justifyContent:"center",
                            borderRadius:4,
                            flexShrink:0,
                        }}
                    >
                        <Newicon fontSize="small"/>
                        <SHQuickAction
                            component={
                                <Box sx={{ ml: 0.5 }}>

                                    <Text variant="red" bold>
                                        {props.config.unavailableReason}
                                    </Text>
                                </Box>
                            }
                        >
                            {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (

                                <Box

                                    aria-owns={anchorEl ? "simple-popover" : undefined}
                                    aria-haspopup="true"
                                    onMouseEnter={!props.config.available? onMouseEnter : null}
                                    onMouseLeave={!props.config.available ? onMouseLeave : null}
                                    onTouchStart={!props.config.available ? onTouchStart : null}
                                    sx={{
                                        position:'absolute',
                                        right:-6,
                                        top:-6,
                                    }}
                                >
                                    {/* <Text sx={{ fontSize: '1.2em' }}>{props.config.icon}</Text> */}
                                    {props.config.available ? (
                                        <CheckCircle sx={{ color: colors.green[400], fontSize: 16 }} />
                                    ) : (
                                        <Cancel sx={{ color: colors.red[400], fontSize: 16 }} />
                                    )}
                                </Box>
                              
                            )}
                        </SHQuickAction>

                    </Box>
                    
                    {/* Label and Description */}
                    <Box 
                    sx={{ 
                        flex: 1,
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1,
                        overflow:"hidden"
                    }}>
                        <Text noBreak fontWeight="medium">{t(props.config.label)}</Text>
                        <Tooltip title={t(props.config.tooltip)} arrow>
                            <HelpIcon sx={{ fontSize: 14, color:  theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                </Box>
                
                {/* Toggle Control */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ToggleButtonGroup
                        color={isEnabled ? "success" : "error"}
                        value={isEnabled ? "true" : "false"}
                        exclusive
                        onChange={(e, value) => props.handleSyncToggle(props.config.key, value)}
                        disabled={!props.config.available || !props.hasModifyPermission}
                        aria-label={t(props.config.label)}
                        className={cx(`${isEnabled}`, {
                            'active': isEnabled
                        })}
                        sx={{
                            background: colors.red[100],
                            padding: 0.5,
                            borderRadius: 5,
                            transition: "250ms",
                            "&.active": {
                                background: colors.green[100],
                                transition: "250ms",
                            },
                            ...(hasError && {
                                border: `2px solid ${colors.red[400]}`
                            })
                        }}
                    >
                        <ToggleButton 
                            sx={{
                                padding: 0,
                                paddingX: 1.5,
                                border: 0,
                                lineHeight: "28px",
                                textTransform: "unset",
                                borderRadius: "28px !important",
                                color: colors.green[400],
                                transition: "250ms",
                                fontSize: '0.8em',
                                "&.Mui-selected,&.Mui-selected:hover": {
                                    background: colors.red[400],
                                    color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="false"
                        >
                            {t('inactive')}
                        </ToggleButton>
                        <ToggleButton 
                            sx={{
                                padding: 0,
                                paddingX: 1.5,
                                border: 0,
                                lineHeight: "28px",
                                textTransform: "unset",
                                borderRadius: "28px !important",
                                color: colors.red[400],
                                transition: "250ms",
                                fontSize: '0.8em',
                                "&.Mui-selected,&.Mui-selected:hover": {
                                    background: colors.green[400],
                                    color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="true"
                        >
                            {t('active')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Text light sx={{ mt: 1,ml:2, }}>
                {t(props.config.description)}
            </Text>
        </Box>
    );
};

const SyncPanel = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    const hasModifyPermission = useSelector((state) => 
        selectUserPermission(state, [
            UserPrivileges.WEBSHOPCREDENTIALS_MANAGE, 
            UserPrivileges.SETTINGS_ALL
        ])
    );

    const [etailizeWebshopInfo, setEtailizeWebshopInfo] = useState({
        id: 0,
        description: "",
        webshop_type: "",
        state: "",
        credentials: {
            marketplace_config: null
        },
        syncOrders: false,
        syncProducts: false,
        syncStock: false,
        syncCreateProducts: false
    });

    const [propagationSettings, setPropagationSettings] = useState({});
    const [syncValidationErrors, setSyncValidationErrors] = useState([]);
    const [isValidating, setIsValidating] = useState(false);

    const [editWebshopCredentials, { isLoading }] = useEditWebshopCredentialsMutation();

    // Memoize marketplace capabilities to prevent infinite loops
    const capabilities = useMemo(() => {
        return getMarketplaceCapabilities(etailizeWebshopInfo?.credentials?.marketplace_config);
    }, [etailizeWebshopInfo?.credentials?.marketplace_config]);
    
    // Memoize sync configurations to prevent recalculation
    const syncConfigurations = useMemo(() => {
        return getSyncOptionConfigurations(capabilities, t);
    }, [capabilities, t]);

    useEffect(() => {
        if (props.data) {
            setEtailizeWebshopInfo(props.data);
            
            // Initialize propagation settings from the root level sync fields
            const existingSyncSettings = {
                syncOrders: props.data.syncOrders,
                syncProducts: props.data.syncProducts,
                syncStock: props.data.syncStock,
                syncCreateProducts: props.data.syncCreateProducts
            };
            
            // Initialize with default settings based on capabilities
            const defaultSettings = getDefaultSyncSettings(capabilities);

            setPropagationSettings({
                ...defaultSettings,
                ...existingSyncSettings
            });
        }
    }, [props.data, capabilities]);

    // Create state updater that validates against capabilities
    const syncStateUpdater = createSyncStateUpdater(setPropagationSettings, capabilities);

    useEffect(() => {
        // Validate sync configuration when settings change
        const errors = validateSyncConfiguration(propagationSettings, capabilities, t);
        setSyncValidationErrors(errors);
    }, [propagationSettings, capabilities, t]);

    const handleSyncToggle = useCallback((key, val) => {
        const config = syncConfigurations.find(c => c.key === key);
        
        // Prevent enabling unsupported sync options
        if (val === 'true' && config && !config.available) {
            console.warn(`Cannot enable ${key}: not supported by marketplace`);
            return;
        }
        
        syncStateUpdater(key, val);
        
        // Update the main webshop info at root level
        setEtailizeWebshopInfo(prev => ({
            ...prev,
            [key]: val === 'true' || val === true
        }));
    }, [syncConfigurations, syncStateUpdater]);

    

    const handleSaveClick = async () => {
        try {
            if (isLoading) return;

            // Prepare the data for the API call with sync settings at root level
            const updateData = {
                ...etailizeWebshopInfo,
                syncOrders: propagationSettings.syncOrders,
                syncProducts: propagationSettings.syncProducts,
                syncStock: propagationSettings.syncStock,
                syncCreateProducts: propagationSettings.syncCreateProducts
            };
            
            const response = await editWebshopCredentials(updateData).unwrap();
            showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    // Mobile actions are handled by the desktop footer in the component structure

    // Mobile actions are handled by the desktop footer in the component structure
    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions:
                <GridActions
                    hasModifyPermission={hasModifyPermission}
                    isLoading={isLoading}
                    action={handleSaveClick}
                />
        })
    },[hasModifyPermission,isLoading,handleSaveClick])

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)": {
                    marginBottom: 11
                }
            }}
        >
            <Dropdown title={t("synchronization_settings")} open>
                <Box sx={{ mb: 3,mt:1, }}>
                    {/* <Text variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {t("sync_description")}
                    </Text> */}

                    {/* Group by category */}
                    {['send', 'receive'].map(category => {
                        const categoryConfigs = syncConfigurations.filter(config => config.category === category);
                        
                        if (categoryConfigs.length === 0) return null;
                        
                        return (
                            <Box key={category} sx={{ mb: 4}}>
                                <Text bold light sx={{ 
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    {t(category === 'send' ? 'send_to_marketplace' : 'receive_from_marketplace')}
                                </Text>
                                
                                <Box
                                    sx={{
                                        display:"flex",
                                        flexDirection:'column',
                                        gap:2,
                                    }}
                                >
                                    {categoryConfigs.map((config,index) => (
                                        
                                        <EtailizeSyncOption 
                                            key={index+"so"}
                                            config={config}
                                            propagationSettings={propagationSettings}
                                            handleSyncToggle={handleSyncToggle}
                                            hasModifyPermission={hasModifyPermission}
                                            syncValidationErrors={syncValidationErrors}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}

                    {/* Validation Errors */}
                    {syncValidationErrors.length > 0 && (
                        <Box sx={{ 
                        }}>
                            <Text bold  sx={{ mb: 1 }}>
                                 {t('configuration_errors')}
                            </Text>
                            {syncValidationErrors.map((error, index) => (
                                <Box
                                    key={index+"sve"}
                                    sx={{
                                        background:colors.red[100],
                                        px:2,
                                        borderRadius:4,
                                        py:0.5,
                                    }}
                                >
                                    <Text  variant={"red"}>
                                        {error.message}
                                    </Text>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Dropdown>
                
            <SpDesktopFooter>
                <GridActions
                    hasModifyPermission={hasModifyPermission}
                    isLoading={isLoading}
                    action={handleSaveClick}
                />
            </SpDesktopFooter>
        </Box>
    );
};

export default SyncPanel;